import React, { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Row, Col, Form } from 'react-bootstrap';
import classNames from 'classnames';
import moment from 'moment';

import { requestInform as requestExchangeRate } from 'api/sfms/exchangeRate';
import {
  requestList as request,
  requestUpload
} from 'api/sfms/rawMaterial/price';
import Flex from 'components/common/Flex';
import CheckPlant from 'components/common-sfims/CheckPlant';
import MonthPicker from 'components/common-sfims/form/MonthPicker';
import ListInContent from 'components/common-sfims/ListInContent';
import ExchangeRateList from 'components/pages/sfms/rawMaterial/price/exchangeRate/List';
import SetExchangeRate from 'components/pages/sfms/rawMaterial/price/exchangeRate/Setting';
import Export from 'components/pages/sfms/rawMaterial/price/Export';
import Upload from 'components/pages/sfms/rawMaterial/price/Upload';
import UploadMarket from 'components/pages/sfms/rawMaterial/price/UploadMarket';
import { datas2ExcelArray, utc2tz } from 'function/convert';
import { downloadExcelByArray } from 'function/file';
import { getText } from 'function/language';
import { getErrorMessage } from 'function/message';
import useCheckPlantState from 'global/checkPlantState';
import { useConfirmModalState } from 'global/confirmModalState';
import useListScroll from 'hooks/useListScroll';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';
import { BEST_MIX_STATUS, RAW_MATERIAL_PRICE_TYPE } from 'types/type';
import isObject from 'utils/isObject';
import { comma, number as formatNumber } from 'utils/format';
import format from 'utils/string/format';

/**
 * 배합비 작성 정보 관리 > 원료 가격 - 목록
 */
const List = () => {
  /* hook */
  const { end, setEnd, total, setTotal } = useListScroll();

  /* router */
  const { state } = useLocation();

  /* recoil */
  const { checkPlant } = useCheckPlantState();
  const { setConfirmModal } = useConfirmModalState();

  /* state */
  // 컬럼 목록
  const [columns, setColumns] = useState([]);
  // 목록
  const [datas, setDatas] = useState([]);
  // 월 환율 목록
  const [exchangeRates, setExchangeRates] = useState([]);
  // 목록 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 월 환율 목록 로딩 여부
  const [isExchangeRatesLoading, setIsExchangeRatesLoading] = useState(true);
  // 환율 가격 히스토리 목록 모달 표시 여부
  const [isShowExchangeRateList, setIsShowExchangeRateList] = useState(false);
  // BestMix에 전송 모달 표시 여부
  const [isShowExport, setIsShowExport] = useState(false);
  // 업로드 모달 표시 여부
  const [isShowUpload, setIsShowUpload] = useState(false);
  // 마켓 가격 업로드 모달 표시 여부
  const [isShowUploadMarket, setIsShowUploadMarket] = useState(false);
  // 목록 메시지
  const [message, setMessage] = useState({});
  // 월 환율 목록 메시지
  const [exchangeRatesMessage, setExchangeRatesMessage] = useState({});
  // 원료 가격 목록 조회 요청 데이터
  const [params, setParams] = useObjReducer({
    factoryCodeSeqList: checkPlant,
    priceYm: moment()
      .add(moment().date() >= 22 ? 1 : 0, 'months')
      .format('YYYYMM'),
    ...state
  });
  // 선택한 월
  const [priceYm, setPriceYm] = useState(state?.priceYm ?? params.priceYm);
  // 원료 코드 검색어
  const [rwmtrCd, setRwmtrCd] = useState(state?.rwmtrCd ?? '');
  // 환율 가격 설정 모달에 전달할 데이터
  const [setExchangeRateModalProps, setSetExchangeRateModalProps] =
    useObjReducer({});
  // 원료 가격 업로드 요청 데이터
  const [uploadData, setUploadData] = useObjReducer({});

  /* watch */
  useEffect(() => {
    requestList();
  }, [params]);
  useEffect(() => {
    const monthFormat = format(getText('ym'), {
      year: 'YY',
      month: 'M'
    });

    setColumns([
      'rowNum',
      'pltCode',
      'nationCurr',
      'rwmtrCd',
      'rwmtrNm',
      {
        text: 'marketPrice',
        childrens: [moment(params.priceYm).format(monthFormat)]
      },
      {
        text: 'stockPrice',
        childrens: new Array(6).fill(0).map((value, index) =>
          moment(params.priceYm)
            .add(value + index, 'months')
            .format(monthFormat)
        )
      },
      'regDt',
      'regUsrNm',
      'bestmixStatus',
      'transDt'
    ]);
    requestExchangeRateInform();
  }, [params.priceYm]);

  /* method */
  // 월 환율 목록 조회 요청
  const requestExchangeRateInform = async () => {
    setExchangeRates([]);
    setIsExchangeRatesLoading(true);

    try {
      const {
        list = [],
        code,
        message
      } = await requestExchangeRate({ exchangeYm: params.priceYm });

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setExchangeRates(list);
    } catch (error) {
      setExchangeRatesMessage(error);
    } finally {
      setIsExchangeRatesLoading(false);
    }
  };
  // 원료 가격 목록 조회 요청
  const requestList = async () => {
    setEnd(20);
    setTotal(0);
    setDatas([]);
    setIsLoading(true);
    setMessage({});

    try {
      const {
        list = [],
        totalCount = 0,
        code,
        message
      } = await request(params);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setTotal(totalCount);
      setDatas(
        list.map((value = {}, index) => ({
          ...value,
          rowNum: totalCount - index,
          ...new Array(6).fill(0).reduce(
            (result, price, index) => ({
              ...result,
              [`price${price + index}`]: value.priceList?.find?.(
                ({ priceYm } = {}) =>
                  moment(params.priceYm)
                    .add(price + index, 'months')
                    .isSame(priceYm, 'month')
              )?.price
            }),
            {}
          ),
          regDt: utc2tz(value.regDt),
          transDt: utc2tz(value.transDt)
        }))
      );
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };
  // 원료 가격 업로드 요청
  const requestUploadData = async () => {
    let title = 'complete';
    let message = 'completeUpload';

    try {
      const { code, message } = await requestUpload(uploadData);

      if (code === RESPONSE_CODE.Error) {
        throw { error: message };
      }

      if (code !== RESPONSE_CODE.Success) {
        throw { code: 'failureUpload' };
      }

      // 원료 가격 목록 조회 요청
      await requestList();
    } catch (error) {
      title = 'failure';
      message = getErrorMessage(error?.code, error?.error);
    } finally {
      setConfirmModal({
        isOpen: true,
        title,
        message
      });
    }
  };

  /* event */
  // 엑셀 다운로드 클릭 시
  const onDownloadExcelClick = () => {
    const format = 'YYMM';

    downloadExcelByArray(
      [
        columns.reduce(
          (result, value) =>
            isObject(value)
              ? [
                  ...result,
                  ...value.childrens.map((children, index) =>
                    index === 0 ? getText(value.text) : ''
                  )
                ]
              : [...result, getText(value)],
          []
        ),
        columns.reduce(
          (result, value) =>
            isObject(value) ? [...result, ...value.childrens] : [...result, ''],
          []
        ),
        ...datas2ExcelArray(datas, [
          'rowNum',
          'pltCode',
          'nationCurr',
          'rwmtrCd',
          'rwmtrNm',
          'price',
          'price0',
          'price1',
          'price2',
          'price3',
          'price4',
          'price5',
          'regDt',
          'regUsrNm',
          'bestmixStatus',
          'transDt'
        ])
      ],
      [
        getText('rawMaterialPrice'),
        moment(params.priceYm).format(format),
        moment(params.priceYm).add(5, 'months').format(format)
      ]
        .join('_')
        .concat('.xlsx'),
      [
        {
          s: {
            r: 0,
            c: 0
          },
          e: {
            r: 1,
            c: 0
          }
        },
        {
          s: {
            r: 0,
            c: 1
          },
          e: {
            r: 1,
            c: 1
          }
        },
        {
          s: {
            r: 0,
            c: 2
          },
          e: {
            r: 1,
            c: 2
          }
        },
        {
          s: {
            r: 0,
            c: 3
          },
          e: {
            r: 1,
            c: 3
          }
        },
        {
          s: {
            r: 0,
            c: 4
          },
          e: {
            r: 1,
            c: 4
          }
        },
        {
          s: {
            r: 0,
            c: 6
          },
          e: {
            r: 0,
            c: 11
          }
        },
        {
          s: {
            r: 0,
            c: 12
          },
          e: {
            r: 1,
            c: 12
          }
        },
        {
          s: {
            r: 0,
            c: 13
          },
          e: {
            r: 1,
            c: 13
          }
        },
        {
          s: {
            r: 0,
            c: 14
          },
          e: {
            r: 1,
            c: 14
          }
        },
        {
          s: {
            r: 0,
            c: 15
          },
          e: {
            r: 1,
            c: 15
          }
        }
      ]
    );
  };
  // 검색 클릭 시
  const onSearchClick = event => {
    event.preventDefault();

    setParams({
      factoryCodeSeqList: checkPlant,
      priceYm,
      rwmtrCd
    });
  };
  // 업로드
  const onUpload = async () => {
    // 스탁 가격 업로드 시
    if (uploadData.uploadPriceType === RAW_MATERIAL_PRICE_TYPE.STOCK) {
      // 원료 가격 업로드 요청
      await requestUploadData();

      return;
    }

    // 마켓 가격 업로드 시 마켓 가격 업로드 모달 표시
    setIsShowUploadMarket(true);
  };
  // 업로드 클릭 시
  const onUploadClick = (uploadPriceType = RAW_MATERIAL_PRICE_TYPE.MARKET) => {
    setUploadData({ uploadPriceType });
    setIsShowUpload(true);
  };

  /* view */
  return (
    <>
      <ListInContent
        columns={columns}
        message={message}
        showLoading={isLoading}
      >
        <Fragment key="headerButton">
          <Button
            size="sm"
            onClick={() => onUploadClick(RAW_MATERIAL_PRICE_TYPE.MARKET)}
          >
            {getText('uploadMarketPrice')}
          </Button>
          <Button
            size="sm"
            className="ms-2"
            onClick={() => onUploadClick(RAW_MATERIAL_PRICE_TYPE.STOCK)}
          >
            {getText('uploadStockPrice')}
          </Button>
          <Button
            size="sm"
            className="ms-2"
            onClick={() => setIsShowExport(true)}
          >
            {getText('exportBestMix')}
          </Button>
        </Fragment>
        <Fragment key="search">
          <CheckPlant value={params.factoryCodeSeqList} />
          <Flex
            alignItems="center"
            className="pt-3 pb-3 px-1 bg-light fs--1 text-700"
          >
            <strong className="fw-bolder">
              {format(getText('monthlyExchangeRate'), {
                year: moment(params.priceYm).year(),
                month: moment(params.priceYm).format('MM')
              })}
            </strong>
            {exchangeRates.length > 0 ? (
              exchangeRates.map(({ rate, currency, nationCode } = {}) => (
                <p key={`exchangeRates.${nationCode}`} className="ms-4 mb-0">
                  <strong className="me-2 fw-bolder">
                    {formatNumber(rate, ',0.00') || '-'}
                  </strong>
                  {currency}
                </p>
              ))
            ) : (
              <p className="ms-4 mb-0">
                {isExchangeRatesLoading
                  ? getText('loading')
                  : exchangeRatesMessage.code
                  ? getErrorMessage(
                      exchangeRatesMessage.code,
                      exchangeRatesMessage.error
                    )
                  : getErrorMessage(RESPONSE_CODE.NotFoundData)}
              </p>
            )}
            <Button
              size="sm"
              variant="outline-success"
              className="ms-auto"
              onClick={() =>
                setSetExchangeRateModalProps({
                  show: true,
                  inform: {
                    exchangeYm: moment(params.priceYm).format('YYYYMM')
                  }
                })
              }
            >
              {getText('insert')}
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              className="ms-2"
              onClick={() => setIsShowExchangeRateList(true)}
            >
              {getText('inform')}
            </Button>
          </Flex>
          <Form onSubmit={onSearchClick}>
            <Row className="ms-0 me-0 mt-3">
              <Col sm={1} className="ps-1 pe-1">
                <MonthPicker value={priceYm} onUpdate={setPriceYm} />
              </Col>
              <Col sm={2} className="ps-1 pe-1">
                <Form.Control
                  size="sm"
                  type="text"
                  value={rwmtrCd}
                  autoFocus
                  placeholder={getText('rwmtrCd')}
                  onChange={({ target: { value } }) => setRwmtrCd(value)}
                />
              </Col>
              <Col
                sm={3}
                className="ps-1 pe-1 d-flex flex-row align-items-center"
              >
                <Button disabled={isLoading} size="sm" type="submit">
                  {getText('search')}
                </Button>
                <p className="mb-0 ms-3 fs--1 fw-bold lh-1">
                  {format(getText('total'), { total: comma(total) })}
                </p>
                {datas.length > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="ms-2"
                    onClick={onDownloadExcelClick}
                  >
                    {getText('downloadExcel')}
                  </Button>
                )}
              </Col>
            </Row>
          </Form>
        </Fragment>
        <Fragment key="datas">
          {datas.slice(0, end).map((value = {}) => (
            <tr key={`datas${value.rowNum}`}>
              <td>{value.rowNum}</td>
              <td>{value.pltCode}</td>
              <td>{value.nationCurr}</td>
              <td>{value.rwmtrCd}</td>
              <td>{value.rwmtrNm}</td>
              <td className="text-end">{formatNumber(value.price, ',0.00')}</td>
              {new Array(6).fill(0).map((price, index) => (
                <td
                  key={`priceList${value.rowNum}-${price + index}`}
                  className="text-end"
                >
                  {formatNumber(value[`price${price + index}`], ',0.00')}
                </td>
              ))}
              <td>{value.regDt}</td>
              <td>{value.regUsrNm}</td>
              <td>
                {value.bestmixStatus ? (
                  <span
                    className={classNames({
                      'text-warning':
                        value.bestmixStatus === BEST_MIX_STATUS.UPLOAD,
                      'text-primary':
                        value.bestmixStatus === BEST_MIX_STATUS.BESTMIX_TRANS
                    })}
                  >
                    {value.bestmixStatus}
                  </span>
                ) : (
                  value.bestmixStatus
                )}
              </td>
              <td>{value.transDt}</td>
            </tr>
          ))}
        </Fragment>
      </ListInContent>
      <Upload
        show={isShowUpload}
        uploadData={uploadData}
        setUploadData={setUploadData}
        onHide={() => setIsShowUpload(false)}
        onUpload={onUpload}
      />
      <UploadMarket
        show={isShowUploadMarket}
        uploadData={uploadData}
        onHide={() => setIsShowUploadMarket(false)}
        onUpload={requestUploadData}
      />
      <Export
        show={isShowExport}
        onHide={() => setIsShowExport(false)}
        onExport={requestList}
      />
      <SetExchangeRate
        {...setExchangeRateModalProps}
        onHide={() => setSetExchangeRateModalProps({ show: false })}
        onSetting={requestExchangeRateInform}
      />
      <ExchangeRateList
        show={isShowExchangeRateList}
        onHide={() => setIsShowExchangeRateList(false)}
      />
    </>
  );
};

export default List;
